const SENSITIVE_KEYWORDS = [
  "password",
  "passcode",
  "token",
  "secret",
  "session",
  "cookie",
  "credential",
  "auth",
  "email",
  "phone",
  "mobile",
  "otp",
  "key",
  "signature",
  "authorization",
  "bearer",
  "jwt",
  "idtoken",
  "accesstoken",
  "refreshtoken",
];

const STRING_PATTERNS: Array<{ regex: RegExp; label: string }> = [
  { regex: /\b[A-F\d]{32,}\b/gi, label: "HEX" },
  { regex: /\b\d{16,}\b/g, label: "NUMBER" },
  { regex: /bearer\s+[a-z0-9\-\._~\+\/]+=*/gi, label: "TOKEN" },
  { regex: /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, label: "JWT" },
  { regex: /eyJ[A-Za-z0-9_\-]{20,}/g, label: "BASE64" },
  { regex: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, label: "EMAIL" },
];

const MAX_DEPTH = 4;
const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_KEYS = 50;

type ConsoleMethod = "log" | "info" | "warn" | "error" | "debug";

type ImportMetaEnv = {
  DEV?: boolean;
  PROD?: boolean;
  MODE?: string;
  [key: string]: unknown;
};

const getImportMetaEnv = (): ImportMetaEnv => {
  try {
    return typeof import.meta !== "undefined" && import.meta && "env" in import.meta
      ? ((import.meta as { env?: ImportMetaEnv }).env ?? {})
      : {};
  } catch {
    return {};
  }
};

const getVerbosePreference = (): boolean => {
  if (typeof window !== "undefined" && "localStorage" in window) {
    try {
      const stored = window.localStorage.getItem("__SAFE_CONSOLE_VERBOSE__");
      if (stored === "true") {
        return true;
      }
      if (stored === "false") {
        return false;
      }
    } catch {
      // ignore storage errors (e.g. private mode)
    }
  }
  const env = getImportMetaEnv();
  if (typeof env.VITE_ENABLE_VERBOSE_SAFE_CONSOLE === "string") {
    return env.VITE_ENABLE_VERBOSE_SAFE_CONSOLE === "true";
  }
  return false;
};

const originalConsole: Record<ConsoleMethod, (...args: unknown[]) => void> = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  debug: console.debug.bind(console),
};

const maskString = (value: string): string => {
  return STRING_PATTERNS.reduce((acc, { regex, label }) => acc.replace(regex, `[REDACTED_${label}]`), value);
};

const sanitizeValue = (value: unknown, depth = 0): unknown => {
  if (value == null) {
    return value;
  }

  if (depth > MAX_DEPTH) {
    return "[REDACTED_DEPTH_LIMIT]";
  }

  if (typeof value === "string") {
    return maskString(value);
  }

  if (typeof value === "number") {
    return value > 999999 ? "[REDACTED_NUMBER]" : value;
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return [];
    }

    const limitedItems = value.slice(0, MAX_ARRAY_ITEMS).map((item) => sanitizeValue(item, depth + 1));
    if (value.length > MAX_ARRAY_ITEMS) {
      limitedItems.push(`[REDACTED_${value.length - MAX_ARRAY_ITEMS}_MORE_ITEMS]`);
    }
    return limitedItems;
  }

  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (!entries.length) {
      return {};
    }

    const result: Record<string, unknown> = {};
    const safeKeywords = SENSITIVE_KEYWORDS.map((keyword) => keyword.trim()).filter(Boolean);
    const limitedEntries = entries.slice(0, MAX_OBJECT_KEYS);

    for (const [key, entryValue] of limitedEntries) {
      const normalizedKey = key.toLowerCase();
      const containsSensitiveKeyword = safeKeywords.some((keyword) => normalizedKey.includes(keyword));
      result[key] = containsSensitiveKeyword ? "[REDACTED]" : sanitizeValue(entryValue, depth + 1);
    }

    if (entries.length > MAX_OBJECT_KEYS) {
      result.__truncated__ = `[REDACTED_${entries.length - MAX_OBJECT_KEYS}_MORE_KEYS]`;
    }

    return result;
  }

  return "[REDACTED_UNKNOWN]";
};

const createSafeMethod =
  (method: ConsoleMethod, enabled: boolean) =>
  (...args: unknown[]): void => {
    if (!enabled) {
      return;
    }
    const sanitizedArgs = args.map((arg) => sanitizeValue(arg));
    originalConsole[method](`[SAFE_CONSOLE:${method.toUpperCase()}]`, ...sanitizedArgs);
  };

export const installSafeConsole = (): void => {
  if ((window as unknown as { __SAFE_CONSOLE__?: boolean }).__SAFE_CONSOLE__) {
    return;
  }

  const allowVerboseLogs = getVerbosePreference();

  console.log = createSafeMethod("log", allowVerboseLogs);
  console.info = createSafeMethod("info", allowVerboseLogs);
  console.warn = createSafeMethod("warn", allowVerboseLogs);
  console.debug = createSafeMethod("debug", allowVerboseLogs);
  console.error = createSafeMethod("error", true);

  (window as unknown as { __SAFE_CONSOLE__?: boolean }).__SAFE_CONSOLE__ = true;
};

export const safeConsole = {
  log: (...args: unknown[]) => console.log(...args),
  info: (...args: unknown[]) => console.info(...args),
  warn: (...args: unknown[]) => console.warn(...args),
  error: (...args: unknown[]) => console.error(...args),
  debug: (...args: unknown[]) => console.debug(...args),
};

export const enableVerboseLogs = (): void => {
  try {
    window.localStorage.setItem("__SAFE_CONSOLE_VERBOSE__", "true");
  } catch {
    /* ignore */
  }
};

export const disableVerboseLogs = (): void => {
  try {
    window.localStorage.setItem("__SAFE_CONSOLE_VERBOSE__", "false");
  } catch {
    /* ignore */
  }
};

